import PropTypes from 'prop-types';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';
import { Icon, ArrowLink } from '@bwoty-web/ui-kit';
import { TRAVEL_AIRPLANE_UP_RIGHT, UI_ARROW_RIGHT } from '@bwoty-web/ui-kit/icons';

import { getCloudinaryTemplateUrl } from '../../../utils/imageUtils';

import * as FlightListLogUtils from '../../../utils/logUtils/FlightListLogUtils';

import './_flightListFlight.scss';

const FlightListFlight = ({
  flightInfo,
  hideFlightTime = false,
  isHomeBound = false,
  isSummaryView = false,
  labels,
  openDetails = () => {},
}) => {
  const isGlobe = useIsGlobetrotter();
  const isMobile = useIsMobile();
  const isSummaryViewAndMobile = isMobile && isSummaryView;
  const isMobileSummaryGlobeView = isMobile && isSummaryView && isGlobe;

  const getNumberOfStopsString = (stops) => {
    if (parseInt(stops, 10) > 1) return `${stops} ${labels.stopPlural}`;
    if (parseInt(stops, 10) > 0) return `${stops} ${labels.stopSingular}`;
    return labels.noLayover;
  };

  const getDaysAfterDepartureString = (days) => {
    if (days < 1) return '';
    return ` (+${days})`;
  };

  const openTransportInfoAndLogClick = () => {
    openDetails(0);
    FlightListLogUtils.flightInfoClick();
  };
  const carrierImageUrl = getCloudinaryTemplateUrl(flightInfo.carrierCloudinaryImage, 100, 120);

  const buildDirectionLabel = () => {
    const labelType = isHomeBound ? 'homebound' : 'outbound';
    const label = isMobile ? `${labels[`${labelType}Short`]}` : `${labels[labelType]}`;

    if (isSummaryViewAndMobile) return `${label}`;
    return `${label} ${labels.boundToLabel} ${flightInfo.destinationAirportName}`;
  };

  const renderStopsLink = () => (
    <ArrowLink
      aria-haspopup="dialog"
      onClick={openTransportInfoAndLogClick}
    >
      <span>{getNumberOfStopsString(flightInfo.totalNumberOfStops)}</span>
    </ArrowLink>
  );

  return (
    <div className="iw-flight-list-flight">
      <div className="iw-flight-list-flight__departure-date-container">
        <span className="iw-flight-list-flight__direction-label">{buildDirectionLabel()}</span>
        {isSummaryViewAndMobile && <span className="iw-flight-list-flight__time">{flightInfo.departureTime}</span>}
        <span className="iw-flight-list-flight__departure-date">{flightInfo.departureDate}</span>
      </div>
      <div className="iw-flight-list-flight__flight-container">
        <div className="iw-flight-list-flight__col1-n-col2-container">
          {!isSummaryViewAndMobile && (
            <div className="iw-flight-list-flight__col1">
              {carrierImageUrl ? (
                <img
                  className="iw-flight-list-flight__carrier-image"
                  alt={flightInfo.carrierName}
                  src={carrierImageUrl}
                />
              ) : (
                <Icon path={TRAVEL_AIRPLANE_UP_RIGHT} />
              )}
            </div>
          )}
          <div className="iw-flight-list-flight__col2">
            {isSummaryViewAndMobile ? (
              <p className="iw-flight-list-flight__carrier">{flightInfo.carrierName}</p>
            ) : (
              <>
                <p className="iw-flight-list-flight__times">
                  {`${flightInfo.departureTime} - ${flightInfo.arrivalTime}${getDaysAfterDepartureString(flightInfo.arrivalDaysAfterDeparture)}`}
                </p>
                <p className="iw-flight-list-flight__carrier">
                  {flightInfo.carrierName}
                  {flightInfo?.hasMultipleCarriers && ` + ${labels.moreCarriers}`}
                </p>
                <span className="iw-flight-list-flight__locations-short">
                  <span className="visually-hidden">{labels.from}:</span>
                  {flightInfo.departureLocationCode}
                  <Icon
                    path={UI_ARROW_RIGHT}
                    className="iw-flight-list-flight__location-divider"
                  />
                  <span className="visually-hidden">, {labels.boundToLabel}:</span>
                  {flightInfo.destinationCode}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="iw-flight-list-flight__col3">
          {!hideFlightTime && (
            <div className="iw-flight-list-flight__duration">{flightInfo?.flightTime && flightInfo.flightTime}</div>
          )}
          <span className="iw-flight-list-flight__locations">
            <span className="visually-hidden">{labels.from}:</span>
            {flightInfo.departureLocation}
            <Icon
              path={UI_ARROW_RIGHT}
              className="iw-flight-list-flight__location-divider"
            />
            <span className="visually-hidden">, {labels.boundToLabel}:</span>
            {flightInfo.destinationAirportName}
          </span>
        </div>
        <div className="iw-flight-list-flight__col4">{renderStopsLink()}</div>
        {!isMobileSummaryGlobeView && (
          <div className="iw-flight-list-flight__col5">
            {isGlobe ? (
              <div className="iw-flight-list-flight__mobile-stops">
                {getNumberOfStopsString(flightInfo.totalNumberOfStops)}
              </div>
            ) : (
              renderStopsLink()
            )}
            {!hideFlightTime && <span className="iw-flight-list-flight__mobile-duration">{flightInfo.flightTime}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

FlightListFlight.propTypes = {
  flightInfo: PropTypes.shape({
    departureDate: PropTypes.string,
    departureTime: PropTypes.string,
    arrivalTime: PropTypes.string,
    arrivalDaysAfterDeparture: PropTypes.number,
    departureLocationCode: PropTypes.string,
    destinationCode: PropTypes.string,
    flightTime: PropTypes.string,
    departureLocation: PropTypes.string,
    destinationAirportName: PropTypes.string,
    totalNumberOfStops: PropTypes.string,
    carrierCloudinaryImage: PropTypes.shape({
      cloudinaryUriTemplate: PropTypes.string,
      size: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    }),
    carrierName: PropTypes.string,
    hasMultipleCarriers: PropTypes.bool,
  }).isRequired,
  labels: PropTypes.shape({
    stopPlural: PropTypes.string,
    stopSingular: PropTypes.string,
    noLayover: PropTypes.string,
    homebound: PropTypes.string,
    homeboundShort: PropTypes.string,
    outbound: PropTypes.string,
    outboundShort: PropTypes.string,
    boundToLabel: PropTypes.string,
    moreCarriers: PropTypes.string,
    from: PropTypes.string,
  }).isRequired,
  isHomeBound: PropTypes.bool,
  openDetails: PropTypes.func,
  hideFlightTime: PropTypes.bool,
  isSummaryView: PropTypes.bool,
};

export default FlightListFlight;
